import type {ReactElement} from 'react';
import {useEffect} from 'react';
import React from 'react';
import styles from '@Components/download-panel/components/download-quality-option/download-quality-option.module.scss';
import {Text, TextSize} from '@Components/text';
import {QualityItem} from '@Components/download-panel/components/quality-item/quality-item';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {CHEAP_PRODUCT_ID, EXPENSIVE_PRODUCT_ID, MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT, getDisplayPriceForProduct} from '@Libraries/poster-download-library';
import {isUserPremium} from '@Libraries/user.library';

enum PDFQuality {
  SMALL = 'small',
  PRINT = 'print',
}

export function DownloadPdfQualityOption(): ReactElement | null {
  const {isPDFWebQuality, selectedPages, needsPurchase, setSelectedProductId, productsData, appleDownloadProducts, setIsPDFWebQuality} = useDownloadPanelData();
  const CHEAP_PRODUCT = productsData?.find((productsData) => productsData.id === String(CHEAP_PRODUCT_ID));
  const EXPENSIVE_PRODUCT = productsData?.find((productsData) => productsData.id === String(EXPENSIVE_PRODUCT_ID));
  useEffect(() => {
    if (selectedPages.length > MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT) {
      setSelectedProductId(EXPENSIVE_PRODUCT_ID);
      return;
    }
    setSelectedProductId(CHEAP_PRODUCT_ID);
  }, [selectedPages.length]);

  const onClickHandler = (id: PDFQuality): void => {
    switch (id) {
      case PDFQuality.PRINT:
        setIsPDFWebQuality(false);
        break;
      case PDFQuality.SMALL:
      default:
        setIsPDFWebQuality(true);
    }
  };

  const getTagPriceText = (): string | undefined => {
    if (!needsPurchase || isUserPremium()) {
      return undefined;
    }
    if (selectedPages.length > MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT) {
      return getDisplayPriceForProduct(EXPENSIVE_PRODUCT, appleDownloadProducts) ?? undefined;
    }
    return getDisplayPriceForProduct(CHEAP_PRODUCT, appleDownloadProducts) ?? undefined;
  };

  return (
    <div className={`flex flex-v-row ${styles.container}`}>
      <Text bold size={TextSize.XSMALL} val={window.i18next.t('pmwjs_quality')} />
      <div className={`flex-h-row ${styles.gap}`}>
        <QualityItem
          onClick={(id): void => {
            onClickHandler(id as PDFQuality);
          }}
          tagText={getTagPriceText()}
          id={PDFQuality.SMALL}
          text={window.i18next.t('pmwjs_standard')}
          isSelected={isPDFWebQuality}
          bullets={[
            {
              text: window.i18next.t('pmwjs_pdf_standard_description_small'),
              showSuccessBullet: false,
            },
          ]}
        />
        <QualityItem
          id={PDFQuality.PRINT}
          tagText={getTagPriceText()}
          text={window.i18next.t('pmwjs_print')}
          onClick={(id): void => {
            onClickHandler(id as PDFQuality);
          }}
          isSelected={!isPDFWebQuality}
          bullets={[
            {
              text: window.i18next.t('pmwjs_pdf_print_description_small'),
              showSuccessBullet: false,
            },
          ]}
        />
      </div>
    </div>
  );
}
